---
title: "Die konsequente Transformation von Läpple Automotive: Wie ein Mittelständler Führungskultur grundlegend neu erfand"
date: "2025-12-24"
excerpt: "Läpple Automotive, Heilbronner Familienunternehmen, tat das Ungewöhnliche: Es ersetzte gezielt alte Führungskräfte durch jüngere, externe Kandidaten. Nicht aus Kostengründen, sondern weil die Unternehmenskultur einen grundlegend anderen Führungsstil brauchte. [Die Fraunhofer-Studie](/blog/forte-modell-fraunhofer-transformationsindex-31-erfolgsfaktoren) zeigt, warum dieses Vorgehen funktioniert – und welche anderen Organisationen es nachmachen sollten."
metaTitle: "Die konsequente Transformation von Läpple Automotive: Wie ein Mittelständler Führungskultur grundlegend neu erfand"
metaDescription: "Läpple Automotive, Heilbronner Familienunternehmen, tat das Ungewöhnliche: Es ersetzte gezielt alte Führungskräfte durch jüngere, externe Kandidaten. Nicht aus Kostengründen, sondern weil die Unternehmenskultur einen grundlegend anderen Führungsstil brauchte. Die Fraunhofer-Studie zeigt, warum dieses Vorgehen funktioniert – und welche anderen Organisationen es nachmachen sollten."
language: "de"
slug: "lapple-automotive-fuehrungskultur-transformation-hierarchie-coaching"
author: "Kevin Rassner"
authorUrl: "https://rassner-dev.org"
canonicalUrl: "https://rassner-dev.org/blog/lapple-automotive-fuehrungskultur-transformation-hierarchie-coaching"
publishedOn: "rassner-dev.org"


---

## Die Läpple-Wende: Von Hierarchie zu Coaching

Läpple Automotive ist nicht bekannt als Revolutionär. Der Heilbronner Mittelständler hat sich seit Jahren durch Spezialisierung und operative Exzellenz bewährt – kein Hippster-Management, kein Agile-Theater. Traditionelle Ingenieur-Organisation.

Und genau deshalb ist das, was Läpple vor sechs Jahren tat, so beeindruckend.

Das Unternehmen erkannte: **Um in der modernen, vernetzten Produktion wettbewerbsfähig zu bleiben, braucht es keinen neuen CEO. Es braucht neue Führungskräfte auf allen Ebenen – mit einem fundamental anderen Mindset.**

Das Ergebnis der Läpple-Transformation ist heute ein **Lehrbuchbeispiel** der Fraunhofer IAO für erfolgreiche, kulturelle Transformation.

## Das Problem: Klassisch-Hierarchischer Führungsstil in einer hybriden Produktionswelt

Frank Scheppe, Chef des operativen Geschäfts bei Läpple, formuliert das Problem prägnant: 

> „Als ich vor sechs Jahren ins Unternehmen kam, war der Führungsstil sehr klassisch-hierarchisch, gerade in den Werken. Einzelne Werks- oder Produktionsleiter machten in ihren Teams die Ansagen, die Untergebenen befolgten sie. Viele Führungskräfte hatten nur ihren Bereich im Blick, nicht das große Ganze. In einer Produktion, die sich immer stärker vernetzte, war das ein Problem."

Das ist nicht böse Absicht. Es ist organisationaler Inzest. Jahrzehnte von funktionierenden Hierarchien schaffen eine **Kultur der Befolgung, nicht der Eigenverantwortung.** Menschen berichten nach oben, nicht lateral. Sie fragen nicht: Wie hilft das dem Ganzen? Sie fragen: Was will mein Chef?

Als diese Produktion digitaler, vernetzter, schneller wird – plötzlich ist das Modell ein Blockkader. Nicht weil die Führungskräfte schlecht sind, sondern weil das System, in dem sie sozialisiert wurden, für eine andere Welt gebaut war.

## Die konsequente Lösung: Strukturelle Erneuerung an kritischen Positionen

Läpple tat das Unkonventionelle: Das Unternehmen entschied sich nicht für ein Leadership-Training, sondern für **systematischen Führungskräfte-Austausch**.

Frank Scheppes Analyse war unbarmherzig: „Das alte Denken musste weg."

Drei konkrete Schritte:

### Schritt 1: Gezielter Austausch auf Werks- und Produktionsleiter-Ebene
Altgediente Mitarbeiter, die sich mit dem neuen Paradigma nicht anfreunden konnten, bekamen drei Optionen:

- **Umschulungsprogramm** zum neuen Führungsstil (für einige erfolgreich)
- **Laterale Umsetzung** in Funktionen ohne Personalverantwortung (die Alternative für viele)
- **Großzügige Abfindung** (für diejenigen, die gehen wollten)

Das war nicht Kosteneinsparung – es war **kulturelle Architektur**. Läpple zahlte für die Umschulungen. Die Unternehmen, die Geld sparen wollen, tun das nicht.

### Schritt 2: Neue Führungskräfte mit anderen Profilen
Statt die „bewährten Kandidaten" mit 30 Jahren Erfahrung zu nehmen, rekrutierte Läpple gezielt Menschen, die...

- ...neugierig waren und neue Wege ausprobierten
- ...andere begeistern und **mitziehen** konnten (nicht: folgen lassen)
- ...oft jünger waren (viele Anfang 30)
- ...bereit waren, ihre Rolle als **Coach, nicht Kommandogeber** zu verstehen

Das ist Kontrapunkt zur klassischen Personalentwicklung. Statt die besten Techniker zum Führungskraft-Trainingsprogramm zu schicken, fragt Läpple: **Wer ist eine Führungspersönlichkeit für die Zukunft?**

### Schritt 3: Systematische Festigung durch einheitliche Trainings und Mentoring
Seit 2018 durchläuft jede Führungskraft bei Läpple das gleiche Trainingsprogramm. Keine Unterscheidung nach Hierarchie-Level – Einheitlichkeit.

Die Inhalte konzentrieren sich auf **drei zentrale Haltungen**:

1. **Die Bedeutung des Verantwortungsbereichs für die Gesamtorganisation verstehen** – Nicht „mein Werk ist autonom", sondern „wie trägt mein Werk zum Ganzen bei?"

2. **Coach-Mentoring statt Mikromanagement** – Führen heißt, die richtigen Menschen auf die richtigen Projekte setzen, nicht jeden Schritt kontrollieren.

3. **Heikle Gespräche führen können** – Insbesondere mit Mitarbeitenden, die Veränderung widerstehen oder nicht verstehen. Statt „das ist Befehl", die Fähigkeit haben zu sagen: „Ich verstehe deine Sorge. Hier ist, warum ich glaube, wir sollten das tun."

Seit 2019 existiert auch ein **Buddy-System**: Jede Führungskraft kann sich einen Mentor aus einer höheren Ebene wählen. Frank Scheppe ist selbst ein Buddy für einen Nachwuchs-Chef.

Worüber sprechen diese Tandems? Seltener Fachthemen. Häufiger: Wie führe ich mein Team durch Unsicherheit? Wie motiviere ich Menschen für Veränderung, deren Job bedroht ist?

## Warum das funktioniert: Die Lernpsychologie der Kulturveränderung

Das klingt aggressiv – alte Führungskräfte rauswerfen, Neue reinbringen. Aber aus Organisationsentwicklungs-Perspektive ist es elegant:

### 1. Man kann Kultur nicht trainieren, man muss sie verkörpern
Ein neuer, junger Fabrikleiter mit agiler Ausbildung verkörpert nicht einfach eine andere **Methode**. Er oder sie verkörpert eine andere **Haltung**. Das ist ansteckend.

Wenn dieser Mensch sein Team befähigt, statt zu kontrollieren – wenn sie Experimente eröffnet, statt Fehler zu bestrafen – dann sehen Menschen: Das ist nicht ein Trainingsprogramm, das ist wie Führung *wirklich* funktioniert hier jetzt.

### 2. Lock-in wird durchbrochen
Eine klassisch-hierarchische Kultur hat massive kulturelle Gravitation. Selbst wenn man 10% der Führungskräfte umschult – die 90% anderen verkörpern das alte System weiter. Die neu geschulte Person wird re-sozialisiert.

Wenn du aber 40% der Führungskräfte systematisch austauschst – und das an kritischen Positionen (Fabrikleiter!) – dann entsteht eine kritische Masse. Die neue Kultur wird zur Norm, nicht zur Anomalie.

### 3. Wissensteilen wird strukturell erzwungen
Als diese neuen Führungskräfte reinkommen, können sie nicht einfach „so weitermachen wie der Vorgänger". Sie brauchen Wissen. Wer gibt es? Die erfahrenen Mitarbeitenden – nicht mehr in Führungspositionen, sondern in Rollen „ohne Personalverantwortung".

Diese Struktur zwingt zu **explizitem Wissenstransfer**. Die alte Fabrikleiterin, die jetzt Technische Beraterin ist, dokumentiert, erklärt, lehrt. Statt Wissen im Kopf zu haben, wird es explizit.

## Die Fraunhofer-Analyse: Warum Läpple als Best Practice gilt

Moritz Maier, Transformationsforscher am Fraunhofer IAO, analysiert es so:

> „Bei Läpple Automotive ist es gelungen, den Führungsstil an neue Anforderungen in der Produktion anzupassen – weg von Top-Down-Befehlsketten, hin zu Führungskräften, die nicht einzelne Mitarbeiter mikromanagen, sondern dafür sorgen, dass alle Rädchen in ihrem Team ineinandergreifen."

Das ist nicht Small Stuff. Das ist ein **Betriebssystem-Wechsel**.

Und der Fraunhofer-Index zeigt: Dies ist nicht ein isoliertes Beispiel. Dies ist eines von 67 analysierten Best-Practice-Transformationen, das zeigt: Wirkliche kulturelle Veränderung braucht manchmal strukturelle Entscheidungen, nicht nur Trainings.

## Die unbequeme Wahrheit für HR und Organisationsentwickler

Diese Fallstudie provoziert eine Frage:

**Wie viele Organisationsentwickler sind bereit, ihre Rolle so konsequent zu begreifen?**

Klassische OD arbeitet mit: Training, Workshops, Interventionen, Coaching der bestehenden Führungskräfte. Das ist nicht schlecht – das ist hilfreich.

Aber bei Läpple hat die OD eine unbequemere Frage gestellt: **Sind die Führungskräfte, die wir heute haben, die richtigen für die Organisation, die wir morgen sein wollen?**

Und wenn die Antwort „Nein" ist – dann ist es manchmal menschlicher und wirksamer, die Kultur durch strukturelle Veränderung zu gestalten statt durch Trainingsprogramme, bei denen die alten Muster doch wieder gewinnen.

## Operative Implikationen

Vier konkrete Fragen für Organisationsentwickler:

**1. Unterscheiden Sie zwischen Trainings-OD und Struktur-OD**
Training kann die Kultur unterstützen. Aber wirkliche kulturelle Veränderung braucht manchmal strukturelle Entscheidungen. Wann ist welche notwendig?

**2. Wagen Sie, die „kritischen Positionen"-Frage zu stellen**
In deiner Organisation – auf welchen Ebenen sitzen Führungskräfte, deren Mindset die Transformation blockiert? Nicht: Sind sie schlecht? Sondern: Sind sie die richtigen für die Zukunft?

**3. Schaffen Sie systematische Mentoring-Strukturen, nicht nur Trainings**
Ein Buddy-System schafft das, was Trainings nicht schaffen: kontinuierliche, reflektive Lernbeziehungen. Nicht punktuelle Events.

**4. Machen Sie den Übergang dignified**
Läpple bot Umschulungen, Umpositionierungen, großzügige Abfindungen an. Das ist nicht kostenlos – aber es respektiert die Menschen, die das alte System gebaut haben, während du es transformierst.

Die Läpple-Story ist nicht „böse Konzern wirft alte Führungskräfte raus". Es ist „intelligente Organisation erkannte, dass kulturelle Transformation manchmal strukturelle Entscheidungen braucht – und es human und strategisch umgesetzt."
